import React, { useContext, useEffect, useMemo, useState } from 'react';
import type { DialogProps as MuiDialogProps } from '@material-ui/core';
import {
  Dialog as MuiDialog,
  DialogActions as MuiDialogActions,
  DialogContent as MuiDialogContent,
  DialogTitle as MuiDialogTitle,
  IconButton
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { CancelOutlined } from '@material-ui/icons';
import { Heading } from '@this/shared/ui/data_displays/typography';
import type { AITTheme } from '@this/shared/ui/theme';

export const ModalBody = MuiDialogContent;
export const ModalFooter = MuiDialogActions;

export type ModalCloseReason = 'backdropClick' | 'escapeKeyDown' | 'buttonClick';
export type ModalOnClose = (event: object, reason: ModalCloseReason) => void;
export const ModalContext = React.createContext<{ onClose?: ModalOnClose }>({});

export const MODAL_SIZE = {
  small: 'small',
  medium: 'medium',
  large: 'large',
  xlarge: 'xlarge'
} as const;
export type ModalSize = typeof MODAL_SIZE[keyof typeof MODAL_SIZE];

export type ModalProps = Omit<MuiDialogProps, 'maxWidth' | 'fullScreen' | 'onClose'> & {
  size?: ModalSize;
  onClose?: ModalOnClose;
  maxWidth?: MuiDialogProps['maxWidth'] | false;
};

const modalSizeMap: Record<ModalSize, MuiDialogProps['maxWidth']> = {
  small: 'xs',
  medium: 'sm',
  large: 'lg',
  xlarge: 'xl'
};

const FULLSCREEN_MODAL_Z_INDEX = 2000000;

type DocumentWithFullscreen = Document & {
  webkitFullscreenElement?: Element | null;
};

const getFullscreenElement = (): HTMLElement | undefined => {
  if (typeof document === 'undefined') {
    return undefined;
  }
  const doc = document as DocumentWithFullscreen;
  const el = doc.fullscreenElement || doc.webkitFullscreenElement || null;
  return el instanceof HTMLElement ? el : undefined;
};

// TODO spの対応
export const Modal = ({
  size = 'medium',
  onClose,
  fullWidth,
  maxWidth,
  container,
  style,
  open,
  ...props
}: ModalProps) => {
  const context = useMemo(
    () => ({
      onClose
    }),
    [onClose]
  );
  const [fullscreenEl, setFullscreenEl] = useState<HTMLElement | undefined>(() => getFullscreenElement());

  useEffect(() => {
    const sync = () => setFullscreenEl(getFullscreenElement());
    sync();
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync);
    };
  }, []);

  const portalContainer = container ?? fullscreenEl;

  return (
    <ModalContext.Provider value={context}>
      <MuiDialog
        maxWidth={maxWidth !== undefined ? maxWidth : modalSizeMap[size]}
        fullWidth={fullWidth === undefined ? true : fullWidth}
        onClose={context.onClose}
        open={open}
        {...props}
        container={portalContainer}
        disableScrollLock={!!fullscreenEl}
        disableEnforceFocus={!!fullscreenEl}
        disableRestoreFocus={!!fullscreenEl}
        style={{
          ...(fullscreenEl ? { zIndex: FULLSCREEN_MODAL_Z_INDEX } : {}),
          ...style
        }}
      />
    </ModalContext.Provider>
  );
};

const useModalHeaderStyle = makeStyles<AITTheme>(theme => ({
  root: {
    position: 'relative',
    backgroundColor: theme.tokens.colors.background.tertiary,
    textAlign: 'center',
    paddingTop: theme.utils.getSpacing(2),
    paddingBottom: theme.utils.getSpacing(2),
    paddingLeft: theme.utils.getSpacing(4),
    paddingRight: theme.utils.getSpacing(4),
    color: theme.tokens.colors.common.white
  },
  closeButton: {
    position: 'absolute',
    right: 4,
    top: 4
  }
}));

export const ModalHeader = ({ className, children }: { children: React.ReactNode; className?: string }) => {
  const styles = useModalHeaderStyle();
  const { onClose: handleClose } = useContext(ModalContext);

  return (
    <MuiDialogTitle disableTypography className={`${className} ${styles.root}`}>
      <Heading level="3">{children}</Heading>
      {handleClose && (
        <IconButton
          aria-label="close"
          className={styles.closeButton}
          onClick={e => handleClose(e, 'buttonClick')}
          color="primary"
          size="small"
        >
          <CancelOutlined />
        </IconButton>
      )}
    </MuiDialogTitle>
  );
};
